import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
  runTransaction,
  increment,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';

const ALREADY_DONE = 'ALREADY_DONE';
const NOT_PENDING = 'NOT_PENDING';

function timestampToMillis(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (ts.seconds != null) {
    return ts.seconds * 1000 + (ts.nanoseconds || 0) / 1e6;
  }
  if (typeof ts.toDate === 'function') return ts.toDate().getTime();
  return 0;
}

/** Mevcut Kasa / Gün Sonu ile aynı: bugünden önceki son gunsonukasa kapanışı */
export async function getLastGunsonuTimestamp(db, sube) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const gunsonuQuery = query(
    collection(db, 'subeler', sube, 'gunsonukasa'),
    where('timestamp', '<', Timestamp.fromDate(today)),
    orderBy('timestamp', 'desc'),
    limit(1)
  );
  const gunsonuSnapshot = await getDocs(gunsonuQuery);
  if (!gunsonuSnapshot.empty) {
    return gunsonuSnapshot.docs[0].data().timestamp || new Timestamp(0, 0);
  }
  return new Timestamp(0, 0);
}

function resolveDusulecekAdet(islem) {
  if (islem.adet && parseInt(islem.adet, 10) > 0) {
    return parseInt(islem.adet, 10);
  }
  return 1;
}

function buildSentetikCikisPayload(islem, sube) {
  return {
    islemNo: `${islem.islemNo}-TESLIM`,
    kategori: islem.kategori,
    urun: islem.urun,
    islemTuru: 'Çıkış',
    musteri: islem.musteri || null,
    firma: islem.firma || null,
    barkod: islem.barkod || null,
    gram: islem.gram || 0,
    adet: islem.adet || 0,
    urunBilgisi: islem.urunBilgisi,
    islemAciklamasi: `Geçmiş Teslimat (Ref: ${islem.islemNo})`,
    hasFiyati: 0,
    bakiye: 0,
    nakit: 0,
    havaleEft: 0,
    krediKarti: 0,
    toplamTutar: 0,
    listeFiyati: 0,
    hasEsdeger: 0,
    teslimatDurumu: 'Teslim Edildi',
    timestamp: serverTimestamp(),
    sube,
    isMulti: false,
    teslimatKaynakIslemId: islem.id || null,
  };
}

async function resolveUrunRef(db, sube, barkod) {
  if (!barkod) return null;
  const urunlerRef = collection(db, 'subeler', sube, 'urunler');
  const q = query(urunlerRef, where('barkod', '==', barkod));
  const querySnapshot = await getDocs(q);
  if (querySnapshot.empty) return null;
  return doc(db, 'subeler', sube, 'urunler', querySnapshot.docs[0].id);
}

function isAlreadyCompleted(islem) {
  return (
    islem.teslimatDurumu === 'Teslim Edildi' ||
    islem.teslimatStokDusuldu === true
  );
}

/**
 * Teslimat onayı: urunler.stok (tek sefer), koşullu sentetik Çıkış, orijinal işlem güncelleme.
 * @returns {{ success: boolean, message: string, alreadyDone?: boolean }}
 */
export async function confirmTeslimatDelivery(db, sube, islemId) {
  if (!sube || !islemId) {
    return { success: false, message: 'Şube veya işlem bilgisi eksik.' };
  }

  const islemRef = doc(db, 'subeler', sube, 'islemler', islemId);
  const preSnap = await getDoc(islemRef);

  if (!preSnap.exists()) {
    return { success: false, message: 'İşlem bulunamadı.' };
  }

  const preData = { id: preSnap.id, ...preSnap.data() };

  if (isAlreadyCompleted(preData)) {
    return {
      success: false,
      alreadyDone: true,
      message: 'Bu teslimat zaten tamamlanmış.',
    };
  }

  if (preData.teslimatDurumu !== 'Teslimat Bekliyor') {
    return { success: false, message: 'Bu işlem teslimat bekleyen durumda değil.' };
  }

  const lastGunsonuTimestamp = await getLastGunsonuTimestamp(db, sube);
  const needSynthetic =
    timestampToMillis(preData.timestamp) < timestampToMillis(lastGunsonuTimestamp);

  const urunRef = await resolveUrunRef(db, sube, preData.barkod);
  const dusulecekMiktar = resolveDusulecekAdet(preData);

  try {
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(islemRef);
      if (!snap.exists()) {
        throw new Error('İşlem bulunamadı.');
      }

      const d = { id: snap.id, ...snap.data() };

      if (isAlreadyCompleted(d)) {
        throw new Error(ALREADY_DONE);
      }
      if (d.teslimatDurumu !== 'Teslimat Bekliyor') {
        throw new Error(NOT_PENDING);
      }

      const islemUpdates = {
        teslimatDurumu: 'Teslim Edildi',
        teslimatStokDusuldu: true,
        teslimEdildiAt: serverTimestamp(),
      };

      if (urunRef) {
        transaction.update(urunRef, { stok: increment(-dusulecekMiktar) });
      }

      if (needSynthetic && !d.teslimatSentetikCikisId) {
        const syntheticRef = doc(collection(db, 'subeler', sube, 'islemler'));
        transaction.set(syntheticRef, buildSentetikCikisPayload(d, sube));
        islemUpdates.teslimatSentetikCikisId = syntheticRef.id;
      }

      transaction.update(islemRef, islemUpdates);
    });

    return { success: true, message: 'Ürün teslim edildi ve stoğu düşüldü.' };
  } catch (error) {
    if (error.message === ALREADY_DONE) {
      return {
        success: false,
        alreadyDone: true,
        message: 'Bu teslimat zaten tamamlanmış.',
      };
    }
    if (error.message === NOT_PENDING) {
      return { success: false, message: 'Bu işlem teslimat bekleyen durumda değil.' };
    }
    throw error;
  }
}
